import mapValues from 'lodash/mapValues';

export const IMG_FALLBACK_WIDTH = 320;

/** provides the look-and-feel of an image display on a product item view */
export const getSquareImage = (
  $container: JQuery<HTMLImageElement>,
  productImage: (MerchantosProductImageModel & { filename?: string }) | null
) => {
  if (productImage) {
    const imgWidth = $container.width() ?? IMG_FALLBACK_WIDTH;
    const fileName = productImage.filename as string;
    const originalExtension = getExtensionFromFilename(fileName);
    const fallbackExtension = allowedImageUploads.WEBP.extension[0].replace('.', '');

    if (imgWidth) {
      $container.attr(
        'src',
        productImage.getResizedUrl({
          format: isJPEG2000File({ filename: fileName })
            ? fallbackExtension
            : originalExtension ?? fallbackExtension,
          crop: 'fill',
          width: Math.round(imgWidth),
          height: Math.round(imgWidth), // make the image square
        })
      );
    }
  }
};

export const parseImageUsage = (usage: ImageUsage) => {
  const {
    defaultMaxImageCount,
    defaultMaxTotalImageSize,
    totalImageSize,
    imageCountHardLimitReached,
    imageCountSoftLimitReached,
    imageCount,
    totalImageSizeHardLimitReached,
    totalImageSizeSoftLimitReached,
  } = usage;

  const booleans = {
    totalImageSizeHardLimitReached,
    totalImageSizeSoftLimitReached,
    imageCountHardLimitReached,
    imageCountSoftLimitReached,
  };

  const integers = { imageCount, defaultMaxImageCount, defaultMaxTotalImageSize, totalImageSize };

  return {
    ...booleans,
    ...mapValues(integers, (value) => parseInt(value, 10)),
  };
};

export const allowedImageUploads = Object.freeze({
  JPEG: {
    mimeType: ['image/jpeg'],
    extension: ['.jpeg', '.jpg'],
  },
  PNG: {
    mimeType: ['image/png'],
    extension: ['.png'],
  },
  WEBP: {
    mimeType: ['image/webp'],
    extension: ['.webp'],
  },
  GIF: {
    mimeType: ['image/gif'],
    extension: ['.gif'],
  },
  JPEG2000: {
    mimeType: ['image/jp2', 'image/jpx'],
    extension: ['.jp2', '.jpf', '.jpx'],
  },
});

export const isJPEG2000File = ({ file, filename }: { file?: File; filename?: string }) => {
  if (!file && !filename) return false;

  if (file) {
    return allowedImageUploads.JPEG2000.mimeType.includes(file.type);
  }
  if (filename) {
    return allowedImageUploads.JPEG2000.extension.includes(`.${filename.split('.').pop()}`);
  }

  return false;
};

export const getExtensionFromFilename = (filename: string) => {
  const extensionMaxLength = 4;
  if (!filename.includes('.') || (filename.split('.').pop()?.length ?? 0) > extensionMaxLength)
    return undefined;
  return filename.split('.').pop();
};

export const sanitizeFileName = (filename: string) => {
  const extractedFileFormat = `.${filename.split('.').pop()}`;

  let sanitizedImageFormat;
  let sanitizedFileName = filename;

  const listImageFormat = Object.values(allowedImageUploads)
    .map((item) => item.extension)
    .flat();

  listImageFormat.forEach((item) => {
    if (extractedFileFormat.startsWith(item) === true) {
      sanitizedImageFormat = item;
    }
  });

  if (sanitizedImageFormat) {
    sanitizedFileName = filename.slice(0, filename.lastIndexOf('.')).concat(sanitizedImageFormat);
  }

  return sanitizedFileName;
};
